import { Context } from '../index';
import { formatValue } from '../../../components/base/common/utils';
import { EndpointParams, StrategyDataRow } from './state';

export const getDataFromActiveTab = (
  context: Context,
  { activeTab, params }: { activeTab: any; params: any },
) => {
  return activeTab === 'partners'
    ? context.actions.rate.getRates(params)
    : context.actions.rate.getStrategies(params);
};

export const getStrategies = (context: Context, params: EndpointParams) => {
  const endpoint = `/api/v0/publishers/${context.state.session.sessionData.orgId}/strategies_v2/`;
  const validParams = context.actions.rate.getValidParams({ params, endpointType: 'strategies' });
  return context.effects.api.get(endpoint, validParams);
};

export const getRates = (context: Context, params: EndpointParams) => {
  const endpoint = `/api/v0/publishers/${context.state.session.sessionData.orgId}/rates_v2/`;
  const validParams = context.actions.rate.getValidParams({ params, endpointType: 'rates' });
  return context.effects.api.get(endpoint, validParams);
};

export const getStrategyProducts = (
  context: Context,
  { params, strategyId }: { params: EndpointParams; strategyId: any },
) => {
  const endpoint = `/api/v0/publishers/${context.state.session.sessionData.orgId}/strategies/
			${strategyId}/products_v2`;

  const validParams = context.actions.rate.getValidParams({
    params,
    endpointType: 'strategy-products',
  });
  return context.effects.api.get(endpoint, validParams);
};

export const updateMerchantPublisherNetwork = (context: Context, params: any) => {
  const endpoint = `/api/v0/publishers/${context.state.session.sessionData.orgId}/update_merchant_publisher_network/`;
  const validParams = context.actions.rate.getValidParams({
    params,
    endpointType: 'merchant-pub-network',
  });
  return context.effects.api.put(endpoint, validParams);
};

export const getValidParams = (
  context: Context,
  { params, endpointType }: { params: EndpointParams; endpointType: any },
) => {
  return Object.keys(params).reduce(
    (acc, curr) =>
      context.state.rate.endpointParams[endpointType].includes(curr)
        ? { ...acc, [curr]: params[curr] }
        : acc,
    {},
  );
};

export const getVisibleDataCount = (
  context: Context,
  {
    dataRows,
    isRetailerOverview,
    isActiveStrategyCount,
    participatingCount,
  }: {
    dataRows: any;
    isRetailerOverview?: any;
    isActiveStrategyCount?: any;
    participatingCount?: any;
  },
) => {
  return dataRows.filter(
    dataRow =>
      context.actions.rate.isVisible({ dataRow, isRetailerOverview, isActiveStrategyCount }) &&
      (!participatingCount ||
        (parseFloat(dataRow.clicks) && dataRow.strategy_status !== 'upcoming')),
  ).length;
};

export const getParticipatingDisplayTxt = (
  context: Context,
  { strategies, isRetailerOverview }: { strategies: any; isRetailerOverview: any },
) => {
  const dataLen = context.actions.rate.getVisibleDataCount({
    dataRows: strategies,
    isRetailerOverview,
    isActiveStrategyCount: false,
    participatingCount: true,
  });
  return dataLen
    ? `Participating in ${dataLen} ${dataLen === 1 ? 'strategy' : 'strategies'}`
    : 'Not participating in any';
};

export const isVisible = (
  context: Context,
  {
    dataRow,
    isRetailerOverview,
    isActiveStrategyCount,
  }: { dataRow: any; isRetailerOverview?: any; isActiveStrategyCount?: any },
) => {
  // The row is always visible if it's a partner
  if ('strategies' in dataRow) return true;

  /*
   * Admins can see:
   * 		- Admin-only strategies and public strategies that are targeted to the given pub or targeted to no pubs
   * 		- Retailers that are in-network for the given pub
   *
   * Public users (publishers) can see:
   * 		- Public strategies that are targeted to the given pub or targeted to no pubs
   * 		- Retailers that are in-network for the given pub
   */
  const adminVisible = dataRow.is_admin_only ? context.state.session.sessionData.isAdminView : true;

  const isTargetedPublisher =
    dataRow.visible_to && dataRow.visible_to.length
      ? dataRow.visible_to.includes(context.state.session.sessionData.orgId)
      : true;

  const isEvergreen = dataRow.strategy_name === 'Evergreen Strategy';

  // Filter strategies that are out of network from overview page
  const inNetwork = !isRetailerOverview || dataRow.merchant_in_network;

  return isEvergreen || (inNetwork && adminVisible && isTargetedPublisher);
};

export const formatStrategyRates = (context: Context, dataRow: StrategyDataRow) => {
  return {
    commission_rate:
      dataRow.pricing_model && dataRow.pricing_model === 1
        ? // Commission rate returned as percent, divide to get decimal
          `${formatValue(dataRow.commission_rate, 'currency')} per click`
        : `${formatValue(dataRow.commission_rate, 'percent', 'commission_rate')} Commission`,
    evergreen_rate:
      dataRow.pricing_model && dataRow.pricing_model === 1
        ? // Commission rate returned as percent, divide to get decimal
          `${formatValue(dataRow.evergreen_rate, 'currency')} per click`
        : `${formatValue(dataRow.evergreen_rate, 'percent', 'commission_rate')} Commission`,
    strategy_bid: dataRow.strategy_bid
      ? `${formatValue(dataRow.strategy_bid, 'currency')} per click`
      : null,
  };
};

export const getStrategyBioColumns = (context: Context, strategy: any) => {
  const formattedRates = context.actions.rate.formatStrategyRates(strategy);
  const dateEnd = strategy.strategy_datetime_end
    ? context.actions.date.formatDate({
        dateObj: strategy.strategy_datetime_end,
        fmt: 'MM/DD/YYYY',
        isUtc: true,
      })
    : 'Present';
  return [
    {
      label: 'Strategy Rate',
      value: formattedRates.strategy_bid
        ? formattedRates.strategy_bid
        : formattedRates.commission_rate,
    },
    {
      label: 'Run Dates',
      value: `${context.actions.date.formatDate({
        dateObj: strategy.strategy_datetime_start,
        fmt: 'MM/DD/YYYY',
        isUtc: true,
      })}-${dateEnd}`,
    },
    {
      label: 'Offered By',
      value: strategy.merchant_name,
    },
  ];
};

export const getMockStrategies = (context: Context) => {
  return [
    {
      merchant_name: 'Ulta Beauty',
      strategy_name: 'A campaign // Electronics // Sub electronics // Audio // Sub audio',
      publisher_epc: '0.15',
      commission_rate: 10.0,
      strategy_datetime_start: '2020-08-11T00:00:00Z',
      strategy_datetime_end: '2020-09-30T23:59:59Z',
      clicks: 3000,
      publisher_total_earnings: '90.00',
      merch_id: 1179,
      strategy_id: 1,
    },
  ];
};

export const getMockStrategyProducts = (context: Context) => {
  return {
    pub_id: 1158,
    stats: [
      {
        product_name: 'SkinCeuticals Daily Moisture, 2 Fl. Oz. | Dermstore',
        image_url: 'https://images.dermstore.com/catalog/100113/300x300/1453.jpg',
        publisher_total_earnings: '10.00',
        clicks: 1500,
        publisher_epc: '0.20',
        product_price: '25.00',
        is_in_stock: true,
      },
      {
        product_name: 'JBL Flip 5 Waterproof Portable Bluetooth Speaker - Blue',
        image_url: 'https://images.crutchfieldonline.com/products/2019/24/109/h109FLIP5BL-F.jpg',
        publisher_total_earnings: '10.00',
        clicks: 15,
        publisher_epc: '0.20',
        store_page_url: 'https://bedbathandbeyond.com',
        product_price: '25.00',
        is_in_stock: true,
      },
      {
        product_name: 'Sub-Zero De-Puffing Eye Mask',
        image_url: null,
        publisher_total_earnings: '10.00',
        clicks: 15,
        publisher_epc: '0.20',
        product_price: '25.00',
        is_in_stock: false,
      },
      {
        product_name: 'Tiger Print Wrap Dress',
        image_url:
          'https://www.logolynx.com/images/logolynx/82/828135c8968a8c03ce05b80f93e37824.png',
        publisher_total_earnings: '10.00',
        clicks: 15,
        publisher_epc: '0.20',
        store_page_url: 'https://bedbathandbeyond.com',
        product_price: '25.00',
        is_in_stock: false,
      },
    ],
    page_info: {
      page: 1,
      per_page: 50,
      total_items: 4,
      total_pages: 1,
    },
  };
};

export const getSelectedFeeType = (context: Context, feeTypes: any) => {
  const feeTypesSelected = feeTypes.reduce((acc, option) => {
    return option.selected ? [...acc, option.value] : acc;
  }, []);
  return feeTypesSelected.toString();
};

export const getSelectedCampaignLabels = (context: Context, campaignLabels: any) => {
  const campaignLabelsSelected = campaignLabels.reduce((acc, option) => {
    return option.selected ? [...acc, option.value] : acc;
  }, []);

  return campaignLabels.length === campaignLabelsSelected.length
    ? null
    : campaignLabelsSelected.join(',');
};

export const getTabSortOptions = (
  context: Context,
  {
    sortOptions,
    activeTab,
    strategyOnlySortParams,
    partnerOnlySortParams = [],
  }: {
    sortOptions: any;
    activeTab: any;
    strategyOnlySortParams?: any;
    partnerOnlySortParams?: any;
  },
) => {
  const excludedFromUpcoming = context.state.session.sessionData.isAdminView
    ? []
    : ['clicks', 'publisher_total_earnings', 'publisher_epc'];
  return sortOptions.filter(
    option =>
      (activeTab.value === 'partners' || !partnerOnlySortParams.includes(option.value)) &&
      (activeTab.value !== 'upcoming strategies' || !excludedFromUpcoming.includes(option.value)) &&
      (['strategies', 'upcoming strategies'].includes(activeTab.value) ||
        !strategyOnlySortParams.includes(option.value)),
  );
};

export const getDateRange = (context: Context, params: any) => {
  // Return current date objects for DatePicker
  return {
    startDate: context.actions.date.dateFromStr(params.date_from),
    endDate: context.actions.date.dateFromStr(params.date_to),
  };
};

export const createDateRangeState = (context: Context, range: any) => {
  const { startDate, endDate } = range;
  const number = -context.actions.date.subtractDates({ d1: startDate, d2: endDate, unit: 'day' });
  return {
    startDate,
    endDate,
    // Offset ranges for peformance comparison
    previousStartDate: context.actions.date.offsetFromDate({
      originalDate: startDate,
      number,
      unit: 'day',
    }),
    previousEndDate: context.actions.date.offsetFromDate({
      originalDate: endDate,
      number,
      unit: 'day',
    }),
  };
};

export const getDateRanges = (context: Context) => {
  // DatePicker Dropdown Options
  const dateOffsetMap = {
    sevenDay: -7,
    thirtyDay: -30,
    ninetyDay: -90,
    thisMonth: -1,
    lastMonth: -2,
    halfYear: -6,
  };
  const presetDateRanges = context.actions.date.getPresetDateRanges();

  return ['sevenDay', 'thirtyDay', 'ninetyDay', 'thisMonth', 'lastMonth', 'halfYear'].map(range => {
    const unit = ['thisMonth', 'lastMonth', 'halfYear'].includes(range) ? 'month' : 'day';
    return {
      ...presetDateRanges[range],
      name: range,
      unit,
      // Offset ranges for peformance comparison
      previousLabel: presetDateRanges[range].label
        .replace('Last', 'Previous')
        .replace('This', 'Last'),
      previousStartDate: context.actions.date.offsetFromDate({
        originalDate: presetDateRanges[range].startDate,
        number: dateOffsetMap[range],
        unit,
      }),
      previousEndDate: context.actions.date.offsetFromDate({
        originalDate: presetDateRanges[range].endDate,
        number: dateOffsetMap[range],
        unit,
      }),
    };
  });
};

export const getStrategyStatus = (context: Context, strategy: any) => {
  const statusMap = {
    upcoming: 'upcoming',
    expired: 'expired',
    running_and_active: 'active',
    running_and_paused: 'paused',
  };
  return statusMap[strategy.strategy_status];
};
